import type { CallsQueryParams } from '@/hooks/useCallsQuery'
import type { DutyBranch } from '@/features/duties/branches'
import type { VisitsQueryParams } from '@/hooks/useVisitsQuery'
import type { FilterCondition } from '@/utils/filterUtils'
import type { EvalTestCasesQueryParams } from '@/features/evals/types'
import type { IngestionRunsQueryParams } from '@/features/ingestion/types'
import type { DutyClientsQueryParams } from '@/hooks/useDutyClientsQuery'
import type { ListQueryParams } from '@/lib/api'
import type { ClinicalDataEndpoint } from '@/features/clinical-data/api/api'
import type {
  CobaltSyncAvailabilityWindows,
  ReportType,
} from '@/features/connections/types'
import type {
  NotificationListParams,
  NotificationListStatus,
} from '@/features/notifications/types'

export const callKeys = {
  all: (orgId: string) => ['calls', orgId] as const,
  list: (orgId: string, params?: CallsQueryParams) =>
    [...callKeys.all(orgId), 'list', params] as const,
  count: (orgId: string, filters?: FilterCondition[]) =>
    [...callKeys.all(orgId), 'count', filters] as const,
}

export const notificationKeys = {
  all: (orgId: string) => ['notifications', orgId] as const,
  // `status` stays its own segment ahead of the filters so a partial key can
  // still address one tab's lists across every filter combination — which is
  // what mark-all-read patches, since it only ever touches the inbox.
  lists: (orgId: string, status: NotificationListStatus) =>
    [...notificationKeys.all(orgId), 'list', status] as const,
  list: (
    orgId: string,
    status: NotificationListStatus,
    params?: NotificationListParams
  ) => [...notificationKeys.lists(orgId, status), params ?? {}] as const,
  unreadCount: (orgId: string) =>
    [...notificationKeys.all(orgId), 'unread-count'] as const,
}

export const conversationKeys = {
  all: (agentId: string) => ['conversations', agentId] as const,
  list: (agentId: string, orgSlug: string | null | undefined) =>
    [...conversationKeys.all(agentId), orgSlug] as const,
  // Prefix for one conversation's messages across org keys — what the
  // invalidations use.
  messagesAll: (agentId: string, conversationId: string | undefined) =>
    ['conversation', agentId, conversationId] as const,
  messages: (
    agentId: string,
    conversationId: string | undefined,
    orgSlug: string | null | undefined
  ) =>
    [
      ...conversationKeys.messagesAll(agentId, conversationId),
      orgSlug,
    ] as const,
}

export const callDetailKeys = {
  all: ['call-detail'] as const,
  detail: (callId: string | null, source: 'supabase' | 'vapi') =>
    ['call-detail', callId, source] as const,
}

export const sessionKeys = {
  detail: (sessionId: string) => ['session-detail', sessionId] as const,
  screenshotsAll: (sessionId: string) =>
    ['session-attempt-screenshots', sessionId] as const,
  screenshots: (
    sessionId: string,
    attemptId: string,
    pageSize: number,
    offset: number
  ) =>
    [
      ...sessionKeys.screenshotsAll(sessionId),
      attemptId,
      pageSize,
      offset,
    ] as const,
}

export const callDutyKeys = {
  all: (orgId: string | undefined) => ['call-duty-options', orgId] as const,
}

export const callRubricKeys = {
  all: (orgId: string | undefined) => ['call-rubric-options', orgId] as const,
}

export const handoffDutyKeys = {
  all: (orgId: string | undefined) => ['handoff-duties', orgId] as const,
}

export const visitKeys = {
  all: (orgId: string | undefined) => ['visits', orgId] as const,
  range: (orgId: string | undefined, params: VisitsQueryParams) =>
    [...visitKeys.all(orgId), 'range', params] as const,
}

export const clientDutyKeys = {
  all: (orgId: string | undefined) => ['client-duties', orgId] as const,
  list: (orgId: string | undefined, patientId: string | undefined) =>
    [...clientDutyKeys.all(orgId), patientId] as const,
}

export const providerOptionKeys = {
  all: (orgId: string | undefined) => ['provider-options', orgId] as const,
}

export const facilityOptionKeys = {
  all: (orgId: string | undefined) => ['facility-options', orgId] as const,
}

/**
 * Which branches a duty has. Outside the `duty` prefix that the card queries
 * share, because a card write cannot create or remove a branch — only a
 * promotion can, and it reports the new list itself.
 */
export const dutySummaryKeys = {
  detail: (orgId: string | null | undefined, dutyId: string | undefined) =>
    ['duty-summary', orgId, dutyId] as const,
}

export const dutyBranchKeys = {
  all: (orgId: string | undefined, dutyId: string | undefined) =>
    ['duty-branches', orgId, dutyId] as const,
  promotionPreview: (orgId: string | undefined, dutyId: string | undefined) =>
    [...dutyBranchKeys.all(orgId, dutyId), 'promotion-preview'] as const,
}

export function dutyQueryKey(
  orgId: string | null | undefined,
  dutyId: string | null | undefined,
  suffix: string
) {
  return ['duty', orgId ?? null, dutyId ?? null, suffix] as const
}

/** `dutyQueryKey` suffixes of a duty's client assignment rules. */
export const ASSIGNMENT_LOGIC_QUERY = 'assignment-logic'
export const EXCLUSION_LOGIC_QUERY = 'exclusion-logic'

/** `dutyQueryKey` suffix of a duty's newest assignment run. */
export const LAST_ASSIGNMENT_RUN_QUERY = 'last-assignment-run'

/** `dutyQueryKey` suffixes of the newest run where each rule moved someone. */
export const LAST_ASSIGNMENT_CHANGE_QUERY = 'last-assignment-change'
export const LAST_EXCLUSION_CHANGE_QUERY = 'last-exclusion-change'

export function dutyScriptQueryKey(
  orgId: string | null | undefined,
  dutyId: string | null | undefined,
  branch: DutyBranch,
  suffix: string
) {
  return [
    ...dutyQueryKey(orgId, dutyId, 'script-branch'),
    branch,
    suffix,
  ] as const
}

export const callTextingKeys = {
  detail: (
    orgId: string | null | undefined,
    dutyId: string,
    branch: DutyBranch | null
  ) =>
    branch === null
      ? dutyQueryKey(orgId, dutyId, 'call-texting')
      : dutyScriptQueryKey(orgId, dutyId, branch, 'call-texting'),
}

export const dutyClientKeys = {
  org: (orgId: string | undefined) => ['duty-clients', orgId] as const,
  all: (orgId: string | undefined, dutyId: string | undefined) =>
    [...dutyClientKeys.org(orgId), dutyId] as const,
  list: (
    orgId: string | undefined,
    dutyId: string | undefined,
    params: DutyClientsQueryParams
  ) => [...dutyClientKeys.all(orgId, dutyId), 'list', params] as const,
}

export const clinicalDataKeys = {
  all: (orgId: string | undefined) => ['clinical-data', orgId] as const,
  list: (
    orgId: string | undefined,
    endpoint: ClinicalDataEndpoint,
    params: ListQueryParams
  ) => [...clinicalDataKeys.all(orgId), endpoint, 'list', params] as const,
}

export const claimsKeys = {
  all: (orgId: string | undefined) => ['claims', orgId] as const,
  list: (orgId: string | undefined, params: ListQueryParams) =>
    [...claimsKeys.all(orgId), 'list', params] as const,
  hydration: (orgId: string | undefined, claimId: string | null | undefined) =>
    [...claimsKeys.all(orgId), 'hydrate', claimId ?? null] as const,
  detail: (orgId: string | undefined, claimId: string | null | undefined) =>
    [...claimsKeys.all(orgId), 'detail', claimId ?? null] as const,
}

export const messageKeys = {
  call: (
    orgId: string | undefined,
    callId: string,
    userId: string | null | undefined
  ) => [...messageKeys.all(orgId), 'call', callId, userId] as const,
  all: (orgId: string | undefined) => ['messages', orgId] as const,
  list: (
    orgId: string | undefined,
    params: unknown,
    userId: string | null | undefined
  ) => [...messageKeys.all(orgId), 'list', params, userId] as const,
  thread: (
    orgId: string | undefined,
    phone: string | null,
    userId: string | null | undefined
  ) => [...messageKeys.all(orgId), 'thread', phone, userId] as const,
}

export const workflowKeys = {
  all: (orgId: string | undefined) => ['workflows', orgId] as const,
  list: (orgId: string | undefined) =>
    [...workflowKeys.all(orgId), 'list'] as const,
  // Nebula's org-scoped catalogue — keyed by org only (never by duty) so
  // browsing duty after duty reuses one cached fetch.
  available: (orgId: string | undefined) =>
    [...workflowKeys.all(orgId), 'available'] as const,
}

export const orgMetricsKeys = {
  all: (orgId: string | undefined) => ['org-metrics', orgId] as const,
}

export const billingKeys = {
  all: (orgId: string) => ['billing', orgId] as const,
  currentUsage: (orgId: string) =>
    [...billingKeys.all(orgId), 'current'] as const,
  monthEndUsage: (orgId: string, period: string) =>
    [...billingKeys.all(orgId), 'month-end', period] as const,
  dutyCallCosts: (orgId: string, period: string) =>
    [...billingKeys.all(orgId), 'duty-call-costs', period] as const,
  credits: (orgId: string, period: string) =>
    [...billingKeys.all(orgId), 'credits', period] as const,
}

export const ingestionKeys = {
  all: (orgId: string | undefined) => ['ingestion', orgId] as const,
  runs: (orgId: string | undefined, params: IngestionRunsQueryParams) =>
    [...ingestionKeys.all(orgId), 'runs', params] as const,
  facets: (orgId: string | undefined) =>
    [...ingestionKeys.all(orgId), 'facets'] as const,
}

// Phone lines are a global (cross-tenant) pool (not org-scoped). Keyed by user
// id so cached super-user data can't bleed to a different account that reuses
// the same SPA session (a disabled query still returns cached data otherwise).
export const phoneLineKeys = {
  all: ['phone-lines'] as const,
  summary: (userId: string | null | undefined) =>
    [...phoneLineKeys.all, 'summary', userId] as const,
  allocationBoard: (userId: string | null | undefined) =>
    [...phoneLineKeys.all, 'allocation-board', userId] as const,
}

export const machineKeys = {
  all: (orgId: string | undefined) => ['machines', orgId] as const,
  list: (orgId: string | undefined) =>
    [...machineKeys.all(orgId), 'list'] as const,
}

export const credentialKeys = {
  all: (orgId: string | undefined) => ['credentials', orgId] as const,
  status: (orgId: string | undefined, credentialType: 'ecw') =>
    [...credentialKeys.all(orgId), credentialType, 'status'] as const,
  validationAll: (orgId: string | undefined, credentialType: 'ecw') =>
    [...credentialKeys.all(orgId), credentialType, 'validation'] as const,
  validation: (
    orgId: string | undefined,
    credentialType: 'ecw',
    candidateVersion: number | null | undefined
  ) =>
    [
      ...credentialKeys.validationAll(orgId, credentialType),
      candidateVersion,
    ] as const,
  reportStatus: (orgId: string | undefined, reportType: ReportType) =>
    [...credentialKeys.all(orgId), 'reports', reportType, 'status'] as const,
}

export const cobaltConnectionKeys = {
  all: (orgId: string | undefined) =>
    ['connections', orgId, 'ecw-cobalt'] as const,
  // Prefix over every availability window — invalidate with this, not with a
  // single `syncs(...)` key, which would leave the other windows' entries
  // fresh.
  syncsAll: (orgId: string | undefined) =>
    [...cobaltConnectionKeys.all(orgId), 'syncs'] as const,
  // Each lookback decides the window its availability count describes,
  // so a different window is a different cache entry.
  syncs: (
    orgId: string | undefined,
    {
      referralLookbackDays,
      orderLookbackDays,
    }: CobaltSyncAvailabilityWindows = {}
  ) =>
    [
      ...cobaltConnectionKeys.syncsAll(orgId),
      referralLookbackDays ?? null,
      orderLookbackDays ?? null,
    ] as const,
  configs: (orgId: string | undefined) =>
    [...cobaltConnectionKeys.all(orgId), 'configs'] as const,
}

export const evaluationRubricKeys = {
  list: (orgId: string | undefined) => ['evaluation-rubrics', orgId] as const,
}

export const snippetKeys = {
  all: (orgId: string | undefined) => ['snippets', orgId] as const,
  // dutyId is part of the key because the same list carries that duty's own
  // wording; sharing one entry would serve one duty another's text.
  list: (orgId: string | undefined, dutyId?: string) =>
    [...snippetKeys.all(orgId), 'list', dutyId ?? null] as const,
  variants: (orgId: string | undefined, snippetId: string | undefined) =>
    [...snippetKeys.all(orgId), 'variants', snippetId ?? null] as const,
  detail: (orgId: string | undefined, snippetId: string | undefined) =>
    [...snippetKeys.all(orgId), 'detail', snippetId ?? null] as const,
  usage: (orgId: string | undefined, snippetId: string | undefined) =>
    [...snippetKeys.all(orgId), 'usage', snippetId ?? null] as const,
}

// The uid_hash signs a user id, so it is keyed by user — not by org like most of
// the keys here. Caching it per org would serve one user another user's
// signature whenever a session outlives an org switch.
export const chameleonKeys = {
  all: ['chameleon'] as const,
  identity: (
    userId: string | null | undefined,
    orgId: string | null | undefined
  ) => [...chameleonKeys.all, 'identity', userId, orgId] as const,
}

export const evalKeys = {
  all: (orgId: string | undefined) => ['evals', orgId] as const,
  testCasesAll: (orgId: string | undefined) =>
    [...evalKeys.all(orgId), 'test-cases'] as const,
  testCases: (orgId: string | undefined, params: EvalTestCasesQueryParams) =>
    [...evalKeys.testCasesAll(orgId), params] as const,
  caseDetail: (orgId: string | undefined, caseId: string | undefined) =>
    [...evalKeys.all(orgId), 'cases', caseId, 'detail'] as const,
  caseAssertions: (orgId: string | undefined, caseId: string | undefined) =>
    [...evalKeys.all(orgId), 'cases', caseId, 'assertions'] as const,
  runs: (orgId: string | undefined) =>
    [...evalKeys.all(orgId), 'runs'] as const,
  runsById: (orgId: string | undefined, runIds: string[]) =>
    [...evalKeys.runs(orgId), 'by-id', runIds] as const,
  caseRuns: (orgId: string | undefined, caseId: string) =>
    [...evalKeys.all(orgId), 'cases', caseId, 'runs'] as const,
  runDetail: (orgId: string | undefined, runId: string | null) =>
    [...evalKeys.runs(orgId), runId, 'detail'] as const,
  runAssertionResults: (orgId: string | undefined, runId: string | null) =>
    [...evalKeys.runs(orgId), runId, 'assertion-results'] as const,
  runCallDetail: (
    orgId: string | undefined,
    callId: string | null,
    providerCallId?: string | null
  ) =>
    [...evalKeys.runs(orgId), 'call-detail', callId, providerCallId] as const,
}

/** Duty selection is sorted so picking A then B keys the same as B then A,
 *  matching how the filter rail compares drafts. */
const dutyScope = (dutyIds: readonly string[]) => dutyIds.toSorted().join(',')

export const dutiesMetricsKeys = {
  all: (orgId: string) => ['duties-metrics', orgId] as const,
  /** Every filter is a request parameter, so every filter keys the cache — a
   *  narrower key serves the previous scope's data after a filter change. */
  overview: (
    orgId: string,
    scope: {
      scopeOrgId: string | null
      dutyIds: readonly string[]
      month: string
      monthTo: string
    }
  ) =>
    [
      ...dutiesMetricsKeys.all(orgId),
      'overview',
      scope.scopeOrgId,
      dutyScope(scope.dutyIds),
      scope.month,
      // Both ends key the cache: without this, widening the span to the same
      // start month would serve the narrower answer from cache.
      scope.monthTo,
    ] as const,
  waypointOverview: (
    orgId: string,
    scope: {
      scopeOrgId: string | null
      dutyIds: readonly string[]
      month: string
      monthTo: string
    }
  ) =>
    [
      ...dutiesMetricsKeys.all(orgId),
      'waypoint-overview',
      scope.scopeOrgId,
      dutyScope(scope.dutyIds),
      scope.month,
      scope.monthTo,
    ] as const,
  /** Every scope's rubric results, for the sweep after a rubric setting changes. */
  rubricsAll: (orgId: string) =>
    [...dutiesMetricsKeys.all(orgId), 'rubrics'] as const,
  rubrics: (
    orgId: string,
    scope: {
      scopeOrgId: string | null
      dutyIds: readonly string[]
      month: string
      monthTo: string
    }
  ) =>
    [
      ...dutiesMetricsKeys.rubricsAll(orgId),
      scope.scopeOrgId,
      dutyScope(scope.dutyIds),
      scope.month,
      scope.monthTo,
    ] as const,

  /** Rows carry no baselines, so the comparison window does not key them. */
  rows: (
    orgId: string,
    scope: { scopeOrgId: string | null; dutyIds: readonly string[] }
  ) =>
    [
      ...dutiesMetricsKeys.all(orgId),
      'rows',
      scope.scopeOrgId,
      dutyScope(scope.dutyIds),
    ] as const,
  renewalsSummary: (orgId: string, scopeOrgId: string | null) =>
    [...dutiesMetricsKeys.all(orgId), 'renewals', scopeOrgId] as const,
}

// Spelled once. A save has to clear every org's copy, so it invalidates from a
// prefix rather than from a key — and a prefix written out by hand at the call
// site stops matching the moment this root is renamed, with nothing failing,
// because an invalidation that matches no key is a no-op.
const PERMISSIONS_ROOT = ['permissions'] as const

export const permissionKeys = {
  all: PERMISSIONS_ROOT,
  grants: (orgId: string | undefined) =>
    [...PERMISSIONS_ROOT, 'grants', orgId] as const,
  /** Every org's grants, for the sweep after a save. */
  grantsAll: [...PERMISSIONS_ROOT, 'grants'] as const,
  // Keyed by org: the answer is derived from the caller's role, and a role is
  // per-organization. Without it, switching orgs serves the previous org's
  // capabilities — stale nav, and a route that lets you in before the API 403s.
  mine: (orgId: string | undefined) =>
    [...PERMISSIONS_ROOT, 'me', orgId] as const,
  /** Every org's answer to "what may I use", for the sweep after a save. */
  mineAll: [...PERMISSIONS_ROOT, 'me'] as const,
}
